import React, { useCallback, useState } from "react";
import Typography from "@mui/material/Typography";
import ReactCodeInput from "react-verification-code-input";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { Box, BoxProps } from "@mui/material";

/**
 * PM5 T1 (chrome, batch "common") — converted from MUI v4 + `makeStyles` to
 * MUI v5+ (`@mui/material`) `styled`/`sx`. See docs/mui-conversion-guide.md.
 *
 * Every `className` prop below keeps its v4 semantics exactly: the caller's
 * class **replaces** the default rule rather than merging with it (the v4 code
 * was `override || classes.default`).
 */

// `react-verification-code-input` ships a broken declaration — `declare const
// ReactCodeInput: ReactCodeInput`, an object type rather than a component —
// which is why the v4 file carried a `@ts-ignore` at the call site. Casting
// once here lets `styled()` wrap it and removes the need for the suppression.
type CodeInputProps = {
  className?: string;
  type?: string;
  fields?: number;
  onComplete?: (value: string) => void;
};
const CodeInput =
  ReactCodeInput as unknown as React.ComponentType<CodeInputProps>;

// was: useStyles().verificationCodeContainer
// The target is a third-party component that only accepts a `className` (no
// `sx` prop), so this is `styled()` rather than an `sx` constant — guide §1.2.
const StyledCodeInput = styled(CodeInput)(({ theme }) => ({
  width: "100% !important",
  fontWeight: 600,
  margin: theme.spacing(1.5, 0),
  "& input": {
    height: "56px !important",
    width: "15.6% !important",
    borderTop: "none",
    borderRight: "none",
    borderLeft: "none",
    borderTopLeftRadius: "0px !important",
    borderBottomLeftRadius: "0px !important",
    borderTopRightRadius: "0px !important",
    borderBottomRightRadius: "0px !important",
    borderBottom: 0,
    backgroundColor: "#444",
    margin: "0 0.5%",
    fontSize: "2rem",
    fontWeight: 500,
    color: "#fff",
    fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    "&:focus": {
      border: "1px solid #99ca3c",
      caretColor: "#fff",
      "&:last-child": {
        borderRight: "1px solid #99ca3c",
      },
    },
    "&:last-child": {
      borderRight: "none",
    },
  },
}));

// NOT PORTED — v4 had `container: {}`, an empty rule. It emitted a class name
// but no declarations, so applying it never changed the rendering. Dropped
// rather than carried across as an empty `sx` (guide §1.2 row 1).

// was: useStyles().submit
// `color: "text.primary"` is NOT in the v4 rule — it restores a v4 default
// (guide §2, "Button default color"). This `<Button>` carries no `color` prop,
// so in v4 it was `color="default"` → `color: theme.palette.text.primary`.
// v5+ deleted `default` and made `primary` the default, which would paint the
// label #99ca3c on the lime `typography.button` background — invisible.
// The literal `@media (min-width:600px)` becomes `breakpoints.up("sm")`;
// `themeV5` pins sm to the v4 value of 600, so the emitted query is identical.
const submitSx = (theme: Theme) => ({
  width: "100%",
  margin: theme.spacing(1, 0),
  [theme.breakpoints.up("sm")]: {
    width: "auto",
  },
  color: "text.primary",
});

// The colour half of the parity restore above, for when the caller supplies
// its own `submitClassName` and the v4 `classes.submit` rule does not apply.
// (Neither current caller's class sets `color`, so nothing is being overridden.)
const submitOverriddenSx = { color: "text.primary" };

// was: useStyles().bodyText
const bodyTextSx = {
  color: "common.white",
  textAlign: "center",
  marginTop: 2,
  marginBottom: 2,
};

const CodeForm = ({
  onSubmit,
  submitButtonText = "Reset Password",
  descText = "Enter this code below to reset your password.",
  containerClassName,
  textClassName,
  verificationCodeContainerClassName,
  submitClassName,
}: {
  onSubmit: (code: string) => void;
  descText?: string;
  submitButtonText?: string;
  containerClassName?: BoxProps["className"];
  textClassName?: BoxProps["className"];
  verificationCodeContainerClassName?: BoxProps["className"];
  submitClassName?: BoxProps["className"];
}) => {
  const [code, setCode] = useState("");

  const isValid = code && code.length === 6;
  const onClick = useCallback(() => {
    onSubmit(code);
  }, [code, onSubmit]);

  // v4 applied EITHER the caller's class OR the default rule, never both, so
  // the default styling has to be switched off wholesale when overridden —
  // hence the component swap rather than a merged className. The prop is fixed
  // per call site, so the element type never changes across a mount.
  const CodeInputElement = verificationCodeContainerClassName
    ? CodeInput
    : StyledCodeInput;

  return (
    <Box className={containerClassName}>
      {descText && (
        <Typography
          variant="body1"
          className={textClassName}
          sx={textClassName ? undefined : bodyTextSx}
        >
          We have sent you an email with a <b>6 digit code</b>.<br />
          <br />
          {descText}
        </Typography>
      )}
      <CodeInputElement
        className={verificationCodeContainerClassName}
        type="number"
        fields={6}
        onComplete={setCode}
      />
      <Button
        className={submitClassName}
        sx={submitClassName ? submitOverriddenSx : submitSx}
        disabled={!isValid}
        type="button"
        onClick={onClick}
      >
        {submitButtonText}
      </Button>
    </Box>
  );
};

export default CodeForm;
